import React from "react";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";
import { Body2, Dot } from "@activecollab/components";
import classnames from "classnames";
import classNames from "classnames";
import PropTypes from "prop-types";
import { EntityLink } from "~/components/EntityLink.js";
import { isInternalLink } from "~/utils/url_helper.jsx";
import ProjectName from "../project/ProjectName.jsx";
import TaskNameContainer from "../tasks/TaskNameContainer.jsx";

const LinkDecorator = (decoratedHref, decoratedText, key) => {
  const isInternal = isInternalLink(decoratedHref);
  return (
    <a
      rel="noreferrer"
      href={decoratedHref}
      key={key}
      target={isInternal ? "_self" : "_blank"}
      data-testid="subtask-link"
      className="tw-break-words tw-overflow-wrap-anywhere"
    >
      {decoratedText}
    </a>
  );
};

const SubtaskName = ({ isTaskNameVisible, isProjectNameVisible, name, taskId, projectId }) => {
  const { t } = useTranslation();

  return (
    <div className="tw-flex tw-items-start tw-w-full tw-min-w-0">
      <div
        className={classNames(
          "tw-flex tw-w-full tw-min-w-0 tw-break-words tw-overflow-wrap-anywhere",
          classNames({
            "tw-max-w-[340px]": isTaskNameVisible,
          })
        )}
      >
        <Body2
          weight="medium"
          className={classNames("tw-break-words tw-overflow-wrap-anywhere tw-w-full tw-break-all", {
            "tw-max-w-[340px]": isTaskNameVisible,
          })}
        >
          <Linkify componentDecorator={LinkDecorator}>{name}</Linkify>
        </Body2>
      </div>
      {isTaskNameVisible ? (
        <div className="tw-flex tw-items-center tw-truncate tw-max-w-[340px]">
          <Dot
            className="tw-mx-2 tw-shrink-0"
            color="var(--color-theme-500)"
            size={4}
          />
          <Body2
            color="tertiary"
            className="tw-truncate tw-max-w-[340px]"
          >
            {t("Task")}: <TaskNameContainer id={taskId} />
          </Body2>
        </div>
      ) : null}
      {isProjectNameVisible ? (
        <div className="tw-flex tw-items-center tw-truncate tw-max-w-[340px]">
          <Dot
            className="tw-mx-2 tw-shrink-0"
            color="var(--color-theme-500)"
            size={4}
          />
          <Body2
            color="tertiary"
            className="tw-truncate tw-max-w-[340px]"
          >
            <ProjectName id={projectId} />
          </Body2>
        </div>
      ) : null}
    </div>
  );
};

SubtaskName.propTypes = {
  isTaskNameVisible: PropTypes.bool,
  isProjectNameVisible: PropTypes.bool,
  name: PropTypes.string,
  taskId: PropTypes.number,
  projectId: PropTypes.number,
};

SubtaskName.defaultProps = {
  isTaskNameVisible: false,
  isProjectNameVisible: false,
};

const SubtaskAsListItem = ({
  assigneeId,
  name,
  pendingListChange,
  isTaskNameVisible,
  isProjectNameVisible,
  taskId,
  isLink,
  projectId,
}) => {
  return (
    <span
      className={classnames(
        "tw-flex tw-flex-1 tw-items-start tw-py-[6.375px] tw-min-w-0 tw-break-words tw-overflow-wrap-anywhere tw-max-w-full tw-flex-1",
        {
          subtask_opacity: pendingListChange,
          subtask_print_content: assigneeId < 1,
        }
      )}
    >
      {isLink ? (
        <EntityLink
          to={`/projects/${projectId}/tasks/${taskId}`}
          hashUrl={`Task-${taskId}-${projectId}`}
          className="tw-no-underline tw-outline-none tw-w-full tw-min-w-0 tw-break-words tw-overflow-wrap-anywhere"
        >
          <SubtaskName
            isTaskNameVisible={isTaskNameVisible}
            isProjectNameVisible={isProjectNameVisible}
            taskId={taskId}
            projectId={projectId}
            name={name}
          />
        </EntityLink>
      ) : (
        <SubtaskName
          isTaskNameVisible={isTaskNameVisible}
          isProjectNameVisible={isProjectNameVisible}
          taskId={taskId}
          projectId={projectId}
          name={name}
        />
      )}
    </span>
  );
};

SubtaskAsListItem.propTypes = {
  assigneeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  taskId: PropTypes.number,
  projectId: PropTypes.number,
  name: PropTypes.string.isRequired,
  isTaskNameVisible: PropTypes.bool,
  isProjectNameVisible: PropTypes.bool,
  isLink: PropTypes.bool,
  pendingListChange: PropTypes.bool,
  dueOn: PropTypes.number,
  isCompleted: PropTypes.bool,
  taskDueOn: PropTypes.number,
};

SubtaskAsListItem.defaultProps = {
  assigneeId: 0,
  isTaskNameVisible: false,
  isProjectNameVisible: false,
  isLink: false,
  pendingListChange: false,
};

SubtaskAsListItem.displayName = "SubtaskAsListItem";

export default SubtaskAsListItem;
